import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getTranslatedItems } from '@/plugins/i18n'
import { getLocalizedItems } from '@/plugins/i18n'

import { useWebSiteStore } from '@/stores/web-site.store'

import { garageApi } from '@/apis/garage.api'

import { useGetOrFetchAsync } from '@/composables/store'

export const useGarageStore = defineStore('garage', () => {
  const webSiteStore = useWebSiteStore()

  const api = garageApi
  const garageReasons = ref([])
  const interventionTypes = ref([])
  const documentProgressStatuses = ref([])
  const infoBikePlateSearchCredit = ref(null)

  const costValorizationsEnum = Object.freeze({
    NORMAL: 0,
    WARRANTY: 1,
    COST: 3 //  when >0.2.91.1 reaches prod set COST to 2 according to CostValorizationEnum in enums.py
  })
  const costValorizations = ref([
    { id: costValorizationsEnum.NORMAL, name: 'common.costValorizations.normal' },
    { id: costValorizationsEnum.WARRANTY, name: 'common.costValorizations.warranty' },
    { id: costValorizationsEnum.COST, name: 'common.costValorizations.cost' },
  ])

  const processingTypesEnum = Object.freeze({
    PROCESSING: 0,
    DETAIL: 1,
  })
  const processingTypes = ref([
    { id: processingTypesEnum.PROCESSING, name: 'common.processingTypes.processing' },
    { id: processingTypesEnum.DETAIL, name: 'common.processingTypes.detail' },
  ])

  const workTypesEnum = Object.freeze({
    EXTERNAL: 0,
    INTERNAL: 1
  })
  const workTypes = ref([
    { id: workTypesEnum.EXTERNAL, name: 'common.workTypes.external' },
    { id: workTypesEnum.INTERNAL, name: 'common.workTypes.internal' },
  ])

  const workValuesEnum = Object.freeze({
    PRICE_LIST: 0,
    DEALER_COST: 1
  })
  const workValues = ref([
    { id: workValuesEnum.PRICE_LIST, name: 'common.workValues.priceList' },
    { id: workValuesEnum.DEALER_COST, name: 'common.workValues.dealerCost' },
  ])

  const workActivityEnum = Object.freeze({
    WORKSHEET_PROCESSING: 0,
    TECHNICAL_COURSE: 1,
    DEMO_RIDE: 2,
    WORKSHOP_REORGANIZATION: 3,
    ATTENDANCE: 4,
    OTHER: 5
  })
  const workActivities = ref([
    { id: workActivityEnum.WORKSHEET_PROCESSING, name: 'common.workActivities.worksheetProcessing' },
    { id: workActivityEnum.TECHNICAL_COURSE, name: 'common.workActivities.technicalCourse' },
    { id: workActivityEnum.DEMO_RIDE, name: 'common.workActivities.demoRide' },
    { id: workActivityEnum.WORKSHOP_REORGANIZATION, name: 'common.workActivities.workshopReorganization' },
    { id: workActivityEnum.ATTENDANCE, name: 'common.workActivities.attendance' },
    { id: workActivityEnum.OTHER, name: 'common.workActivities.other' },
  ])

  const fetchInfoBikePlateSearchCreditsAsync = async(force = false) => {
    await useGetOrFetchAsync(infoBikePlateSearchCredit, () => webSiteStore.api.getCreditsAsync({ credit_type: webSiteStore.platformCreditsType.PLATE }), force)
  }

  const fetchGarageReasonsAsync = async() => {
    const res = await garageApi.getGarageReasonsAsync()
    if (res.data) {
      garageReasons.value = res.data
    }
  }

  const fetchDocumentProgressStatusesAsync = async() => {
    const res = await garageApi.getDocumentProgressStatusesAsync()
    if (res.data) {
      documentProgressStatuses.value = res.data
    }
  }

  const fetchInterventionTypesAsync = async() => {
    const res = await garageApi.getInterventionTypesAsync()
    if (res.data) {
      interventionTypes.value = res.data
    }
  }

  const generateWorksheetAsync = async(data) => {
    await garageApi.generateWorksheetFromQuoteAsync(data)
  }

  const getDefaultCostValorization = () => costValorizationsEnum.NORMAL
  const getLocalizedInterventionTypes = () => getTranslatedItems(interventionTypes.value)
  const getLocalizedDocumentProgressStatuses = () => getTranslatedItems(documentProgressStatuses.value).sort((a, b) => a.type - b.type || a.order - b.order)
  const getLocalizedCostValorizations = () => getLocalizedItems(costValorizations.value)
  const getLocalizedProcessingTypes = () => getLocalizedItems(processingTypes.value)
  const getLocalizedGarageReasons = () => getTranslatedItems(garageReasons.value)
  const getLocalizedWorkTypes = () => getLocalizedItems(workTypes.value)
  const getLocalizedWorkValues = () => getLocalizedItems(workValues.value)
  const getLocalizedWorkActivities = () => getLocalizedItems(workActivities.value)

  const $reset = () => {
    garageReasons.value = []
  }

  return {
    api,
    costValorizations,
    costValorizationsEnum,
    documentProgressStatuses,
    infoBikePlateSearchCredit,
    interventionTypes,
    processingTypes,
    processingTypesEnum,
    garageReasons,
    workTypes,
    workTypesEnum,
    workValues,
    workValuesEnum,
    workActivities,
    workActivityEnum,
    fetchInfoBikePlateSearchCreditsAsync,
    fetchGarageReasonsAsync,
    fetchDocumentProgressStatusesAsync,
    fetchInterventionTypesAsync,
    getDefaultCostValorization,
    getLocalizedCostValorizations,
    getLocalizedDocumentProgressStatuses,
    getLocalizedInterventionTypes,
    getLocalizedProcessingTypes,
    getLocalizedGarageReasons,
    getLocalizedWorkTypes,
    getLocalizedWorkValues,
    getLocalizedWorkActivities,
    generateWorksheetAsync,
    $reset
  }
}, {
  persist: [
    {
      pick: ['garageReasons', 'interventionTypes', 'documentProgressStatuses', 'infoBikePlateSearchCredit'],
      storage: localStorage
    }
  ]
})
